import React, { useEffect } from "react";

const BlogLayout = () => {
  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div style={styles.page}>
      {/* ===== Banner Section ===== */}
      <div style={styles.bannerSection}>
        <div style={styles.bannerOverlay}></div>
        <div style={styles.bannerContent}>
          <h1 style={styles.bannerTitle} className="slide-in">
            The Future of Cloud Computing
          </h1>
          <p style={styles.bannerText} className="slide-in">
            Shaping Tomorrow’s Digital World with AI, Edge, and Quantum Cloud
          </p>
        </div>
      </div>

      {/* ===== Blog Container ===== */}
      <div style={styles.container}>
        <div style={styles.content}>
          <h1 style={styles.title}>
            The Future of Cloud Computing: Driving Innovation in the Digital Era
          </h1>

          {/* Author & Meta */}
          <div style={styles.meta}>
            <img
              src="/images/author.jpg"
              alt="Author"
              style={styles.authorImage}
            />
            <div>
              <p style={styles.author}>By John Miller</p>
              <p style={styles.details}>
                <i className="far fa-calendar-alt" style={styles.iconInline}></i>{" "}
                23 Aug 2025 &nbsp; | &nbsp;
                <i className="far fa-clock" style={styles.iconInline}></i> 8 min
                read &nbsp; | &nbsp;
                <i className="far fa-eye" style={styles.iconInline}></i> 1,240
                Views
              </p>
            </div>
          </div>

          {/* Blog Intro */}
          <p style={styles.text}>
            <i className="fas fa-cloud" style={styles.iconInline}></i>
            Cloud computing has become the backbone of the digital economy.
            From hosting applications to enabling AI-driven analytics, the cloud
            has transformed how businesses innovate and scale. But the future of
            cloud computing goes far beyond storage and servers—it’s about
            intelligent, decentralized, and sustainable ecosystems.
          </p>
          <p style={styles.text}>
            <i className="fas fa-bolt" style={styles.iconInline}></i>
            Emerging technologies like edge computing, quantum processing, and
            AI integration will redefine cloud platforms, making them faster,
            smarter, and more secure than ever.
          </p>

          {/* Why It Matters */}
          <h2 style={styles.subheading}>Why the Future of Cloud Computing Matters</h2>
          <ul style={styles.list}>
            <li>
              <i className="fas fa-chart-line" style={styles.iconList}></i> 
              Enables global digital transformation at scale
            </li>
            <li>
              <i className="fas fa-shield-alt" style={styles.iconList}></i>{" "}
              Strengthens security through zero-trust architectures
            </li>
            <li>
              <i className="fas fa-recycle" style={styles.iconList}></i> 
              Supports green computing and sustainable IT operations
            </li>
            <li>
              <i className="fas fa-rocket" style={styles.iconList}></i> 
              Accelerates innovation with AI, IoT, and 5G integration
            </li>
          </ul>

          {/* Key Technologies */}
          <h2 style={styles.subheading}>Key Technologies Shaping the Next-Gen Cloud</h2>
          <p style={styles.text}>
            The evolution of cloud computing will be driven by these cutting-edge
            technologies:
          </p>
          <ul style={styles.list}>
            <li>
              <i className="fas fa-brain" style={styles.iconList}></i>{" "}
              AI and Machine Learning for intelligent automation
            </li>
            <li>
              <i className="fas fa-network-wired" style={styles.iconList}></i>{" "}
              Edge Computing for low-latency applications
            </li>
            <li>
              <i className="fas fa-atom" style={styles.iconList}></i>{" "}
              Quantum Cloud for solving complex problems at scale
            </li>
            <li>
              <i className="fas fa-database" style={styles.iconList}></i>{" "}
              Hybrid and Multi-Cloud for flexibility and resilience
            </li>
          </ul>

          {/* Example */}
          <h2 style={styles.subheading}>Example: Smart Cities Powered by Cloud</h2>
          <p style={styles.text}>
            <i className="fas fa-city" style={styles.iconInline}></i>
            Future smart cities will rely on cloud and edge computing to manage
            traffic, energy, and public services in real time. Connected sensors
            will generate massive data streams, analyzed instantly on the cloud
            to improve efficiency, safety, and sustainability.
          </p>

          {/* Best Practices */}
          <h2 style={styles.subheading}>Best Practices for Adopting Future Cloud Models</h2>
          <p style={styles.text}>
            <i className="fas fa-lightbulb" style={styles.iconInline}></i>
            Businesses embracing next-generation cloud strategies should follow
            these best practices:
          </p>
          <ul style={styles.list}>
            <li>
              <i className="fas fa-layer-group" style={styles.iconList}></i> 
              Adopt hybrid and multi-cloud environments for flexibility
            </li>
            <li>
              <i className="fas fa-user-shield" style={styles.iconList}></i>{" "}
              Implement zero-trust and advanced encryption
            </li>
            <li>
              <i className="fas fa-solar-panel" style={styles.iconList}></i> 
              Prioritize green cloud providers for sustainability
            </li>
            <li>
              <i className="fas fa-users" style={styles.iconList}></i> 
              Upskill workforce for AI and cloud-native development
            </li>
          </ul>

          {/* Future */}
          <h2 style={styles.subheading}>The Road Ahead: Cloud 2030</h2>
          <p style={styles.text}>
            <i className="fas fa-globe" style={styles.iconInline}></i>
            By 2030, cloud platforms will become autonomous, self-healing, and
            deeply integrated with AI. Decentralized cloud powered by blockchain,
            along with quantum-enhanced computation, will enable breakthroughs in
            healthcare, finance, manufacturing, and beyond.
          </p>

          {/* Conclusion */}
          <h2 style={styles.subheading}>Conclusion</h2>
          <p style={styles.text}>
            <i className="fas fa-check-circle" style={styles.iconInline}></i>
            The future of cloud computing is not just about efficiency—it’s
            about shaping the foundation of digital societies. Organizations that
            innovate with AI, edge, and quantum-enabled cloud solutions will
            unlock competitive advantages and drive the next era of global
            progress.
          </p>
        </div>
      </div>

      {/* ===== Animation CSS ===== */}
      <style>
        {`
          @keyframes slideIn {
            0% {
              opacity: 0;
              transform: translateX(-40px);
            }
            100% {
              opacity: 1;
              transform: translateX(0);
            }
          }
          .slide-in {
            animation: slideIn 1.2s ease-out forwards;
          }
        `}
      </style>
    </div>
  );
};

const styles = {
  page: {
    position: "relative",
    background: "#fff",
    fontFamily: "Arial, sans-serif",
    letterSpacing: "0px",
  },
  /* ===== Banner Styles ===== */
  bannerSection: {
    position: "relative",
    width: "100%",
    height: "90vh",
    backgroundImage: "url('/image/futcloud.webp')", 
    backgroundSize: "cover",
    backgroundPosition: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    padding: "0 10%",
    color: "#fff",
  },
  bannerOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0,0,0,0.5)",
  },
  bannerContent: {
    position: "relative",
    zIndex: 2,
    maxWidth: "600px",
  },
  bannerTitle: {
    fontSize: "42px",
    fontWeight: "bold",
    marginBottom: "15px",
    lineHeight: "1.3",
    letterSpacing:"0px",
    color:"white",
  },
  bannerText: {
    fontSize: "18px",
    lineHeight: "1.6",
    maxWidth: "500px",
    textAlign:"justify",
  },

  /* ===== Blog Styles ===== */
  container: {
    display: "flex",
    justifyContent: "center",
    padding: "40px 20px",
    maxWidth: "900px",
    margin: "auto",
  },
  content: {
    flex: 1,
  },
  title: {
    fontSize: "32px",
    fontWeight: "bold",
    marginBottom: "15px",
    lineHeight: "1.3",
    letterSpacing:"0px",
    
  },
  meta: {
    display: "flex",
    alignItems: "center",
    marginBottom: "20px",
    gap: "12px",
  },
  authorImage: {
    width: "55px",
    height: "55px",
    borderRadius: "50%",
    objectFit: "cover",
  },
  author: {
    margin: 0,
    fontWeight: "bold",
    fontSize: "15px",
  },
  details: {
    margin: 0,
    fontSize: "14px",
    color: "#666",
  },
  text: {
    fontSize: "16px",
    lineHeight: "1.6",
    color: "#333",
    marginBottom: "15px",
    textAlign: "justify",
  },
  subheading: {
    fontSize: "22px",
    fontWeight: "600",
    marginTop: "25px",
    marginBottom: "12px",
    letterSpacing:"0px",
  },
  list: {
    paddingLeft: "0px",
    marginBottom: "20px",
    listStyle: "none",
  },
  iconList: {
    marginRight: "8px",
    color: "#007bff",
  },
  iconInline: {
    marginRight: "6px",
    color: "#007bff",
  },

  /* ===== Responsive ===== */
  "@media (max-width: 768px)": {
    bannerTitle: {
      fontSize: "28px",
    },
    bannerText: {
      fontSize: "16px",
    },
    title: {
      fontSize: "26px",
    },
  },
};

export default BlogLayout;
